import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';
import { 
  Users, 
  MapPin, 
  Clock, 
  Route as RouteIcon,
  Mountain,
  CornerDownRight,
  Heart,
  Search,
  Loader2,
  Globe,
  Star,
  MessageCircle,
  Copy,
  User,
  Send,
  X,
  ChevronDown,
  ChevronUp,
  Trophy
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CommunityRoutes = () => {
  const { user } = useAuth();
  const [routes, setRoutes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTag, setSelectedTag] = useState(null);
  const [popularTags, setPopularTags] = useState([]);
  const [selectedRoute, setSelectedRoute] = useState(null);
  const [comments, setComments] = useState([]);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [newComment, setNewComment] = useState({ content: '', rating: 5 });
  const [userProfile, setUserProfile] = useState(null);
  const [profileDialogOpen, setProfileDialogOpen] = useState(false);
  const [expandedRouteId, setExpandedRouteId] = useState(null);

  useEffect(() => {
    fetchRoutes();
    fetchTags();
  }, [selectedTag]);

  const fetchRoutes = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (selectedTag) params.append('tag', selectedTag);
      if (searchQuery) params.append('search', searchQuery);
      
      const response = await axios.get(`${API}/community/routes?${params}`);
      setRoutes(response.data.routes);
    } catch (error) {
      console.error('Failed to fetch community routes:', error);
      toast.error('Failed to load community routes');
    } finally {
      setLoading(false);
    }
  };

  const fetchTags = async () => {
    try {
      const response = await axios.get(`${API}/community/tags`);
      setPopularTags(response.data.tags);
    } catch (error) {
      console.error('Failed to fetch tags:', error);
    }
  };

  const fetchComments = async (routeId) => {
    setCommentsLoading(true);
    try {
      const response = await axios.get(`${API}/community/routes/${routeId}/comments`);
      setComments(response.data.comments);
    } catch (error) {
      console.error('Failed to fetch comments:', error);
    } finally {
      setCommentsLoading(false);
    }
  };

  const fetchUserProfile = async (userId) => {
    try {
      const response = await axios.get(`${API}/users/${userId}/profile`);
      setUserProfile(response.data);
      setProfileDialogOpen(true);
    } catch (error) {
      console.error('Failed to fetch user profile:', error);
      toast.error('Failed to load user profile');
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchRoutes();
  };

  const handleLike = async (routeId) => {
    try {
      const response = await axios.post(`${API}/community/routes/${routeId}/like`);
      setRoutes(routes.map(r => 
        r.id === routeId 
          ? { ...r, likes: response.data.likes, liked_by: response.data.liked ? [...(r.liked_by || []), user.id] : (r.liked_by || []).filter(id => id !== user.id) }
          : r
      ));
    } catch (error) {
      console.error('Failed to like route:', error);
      toast.error('Failed to update like');
    }
  };

  const handleCopyRoute = async (routeId) => {
    try {
      await axios.post(`${API}/community/routes/${routeId}/copy`);
      toast.success('Route copied to your collection!');
    } catch (error) {
      console.error('Failed to copy route:', error);
      toast.error('Failed to copy route');
    }
  };

  const handleAddComment = async (routeId) => {
    if (!newComment.content.trim()) return;

    try {
      const response = await axios.post(`${API}/community/routes/${routeId}/comments`, newComment);
      setComments([response.data, ...comments]);
      setNewComment({ content: '', rating: 5 });
      toast.success('Comment added!');
    } catch (error) {
      console.error('Failed to add comment:', error);
      toast.error('Failed to add comment');
    }
  };

  const handleDeleteComment = async (routeId, commentId) => {
    try {
      await axios.delete(`${API}/community/routes/${routeId}/comments/${commentId}`);
      setComments(comments.filter(c => c.id !== commentId));
      toast.success('Comment deleted');
    } catch (error) {
      console.error('Failed to delete comment:', error);
      toast.error('Failed to delete comment');
    }
  };

  const toggleRouteExpansion = async (routeId) => {
    if (expandedRouteId === routeId) {
      setExpandedRouteId(null);
      setComments([]);
    } else {
      setExpandedRouteId(routeId);
      await fetchComments(routeId);
    }
  };

  const isLiked = (route) => {
    return route.liked_by?.includes(user?.id);
  };

  const renderStars = (rating, interactive = false, onRate = null) => {
    return (
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            type="button"
            onClick={() => interactive && onRate && onRate(star)}
            className={`${interactive ? 'cursor-pointer hover:scale-110' : 'cursor-default'} transition-transform`}
            disabled={!interactive}
          >
            <Star 
              className={`w-4 h-4 ${star <= rating ? 'text-amber-500 fill-amber-500' : 'text-muted-foreground'}`} 
            />
          </button>
        ))}
      </div>
    );
  };

  const getReputationBadge = (reputation) => {
    if (reputation >= 100) return { label: 'Expert Navigator', color: 'bg-amber-500' };
    if (reputation >= 50) return { label: 'Road Master', color: 'bg-blue-500' };
    if (reputation >= 20) return { label: 'Explorer', color: 'bg-green-500' };
    return { label: 'Newcomer', color: 'bg-muted' };
  };

  if (loading && routes.length === 0) {
    return (
      <div className="h-full flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="h-full bg-background overflow-auto">
      <div className="p-6 md:p-8 max-w-6xl mx-auto">
        <div className="mb-8">
          <h1 className="font-display text-3xl font-bold text-foreground flex items-center gap-3">
            <Users className="w-8 h-8 text-primary" />
            Community Routes
          </h1>
          <p className="text-muted-foreground mt-2">
            Discover and share classic car friendly routes with fellow enthusiasts.
          </p>
        </div>

        {/* Search and Filters */}
        <div className="mb-6 space-y-4">
          <form onSubmit={handleSearch} className="flex gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search routes..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 bg-muted/50 border-border"
                data-testid="community-search-input"
              />
            </div>
            <Button type="submit" className="bg-primary hover:bg-primary/90 text-primary-foreground" data-testid="community-search-btn">
              Search
            </Button>
          </form>

          {/* Tags */}
          {popularTags.length > 0 && (
            <div className="flex flex-wrap gap-2">
              <Badge 
                variant={selectedTag === null ? "default" : "outline"}
                className={`cursor-pointer ${selectedTag === null ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'}`}
                onClick={() => setSelectedTag(null)}
              >
                All Routes
              </Badge>
              {popularTags.map((tag) => (
                <Badge 
                  key={tag.name}
                  variant={selectedTag === tag.name ? "default" : "outline"}
                  className={`cursor-pointer ${selectedTag === tag.name ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'}`}
                  onClick={() => setSelectedTag(tag.name)}
                  data-testid={`tag-${tag.name}`}
                >
                  {tag.name} ({tag.count})
                </Badge>
              ))}
            </div>
          )}
        </div>

        {routes.length === 0 ? (
          <Card className="bg-card border-border">
            <CardContent className="py-16 text-center">
              <Globe className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-medium text-foreground mb-2">No shared routes yet</h3>
              <p className="text-muted-foreground">
                Be the first to share a route with the community!
              </p>
            </CardContent>
          </Card>
        ) : (
          <ScrollArea className="h-[calc(100vh-320px)]">
            <div className="space-y-4 pr-4">
              {routes.map((route) => (
                <Card key={route.id} className="bg-card border-border hover:border-primary/30 transition-colors">
                  <CardHeader className="pb-2">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <CardTitle className="font-display text-lg text-foreground">
                          {route.name}
                        </CardTitle>
                        <CardDescription className="flex items-center gap-2 mt-1">
                          <MapPin className="w-3 h-3" />
                          {route.start_location?.name?.split(',')[0]} → {route.end_location?.name?.split(',')[0]}
                        </CardDescription>
                      </div>
                      <div className="flex gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-muted-foreground hover:text-primary"
                          onClick={() => handleCopyRoute(route.id)}
                          data-testid={`copy-route-${route.id}`}
                        >
                          <Copy className="w-4 h-4 mr-1" />
                          Save
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className={`${isLiked(route) ? 'text-red-500' : 'text-muted-foreground'} hover:text-red-500`}
                          onClick={() => handleLike(route.id)}
                          data-testid={`like-route-${route.id}`}
                        >
                          <Heart className={`w-4 h-4 mr-1 ${isLiked(route) ? 'fill-current' : ''}`} />
                          {route.likes || 0}
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="pt-2">
                    {route.description && (
                      <p className="text-sm text-muted-foreground mb-3 line-clamp-2">
                        {route.description}
                      </p>
                    )}

                    <div className="flex flex-wrap gap-4 mb-3">
                      <div className="flex items-center gap-2 text-sm">
                        <RouteIcon className="w-4 h-4 text-muted-foreground" />
                        <span className="text-foreground font-medium">
                          {route.distance?.toFixed(1)} km
                        </span>
                      </div>
                      <div className="flex items-center gap-2 text-sm">
                        <Clock className="w-4 h-4 text-muted-foreground" />
                        <span className="text-foreground font-medium">
                          {Math.round(route.duration || 0)} min
                        </span>
                      </div>
                    </div>

                    {route.warnings?.length > 0 && (
                      <div className="flex flex-wrap gap-2 mb-3">
                        {route.warnings.filter(w => w.type === 'gradient').length > 0 && (
                          <Badge variant="outline" className="bg-amber-500/10 text-amber-500 border-amber-500/30 text-xs">
                            <Mountain className="w-3 h-3 mr-1" />
                            {route.warnings.filter(w => w.type === 'gradient').length} Gradients
                          </Badge>
                        )}
                        {route.warnings.filter(w => w.type === 'bend').length > 0 && (
                          <Badge variant="outline" className="bg-amber-500/10 text-amber-500 border-amber-500/30 text-xs">
                            <CornerDownRight className="w-3 h-3 mr-1" />
                            {route.warnings.filter(w => w.type === 'bend').length} Bends
                          </Badge>
                        )}
                      </div>
                    )}

                    {route.tags?.length > 0 && (
                      <div className="flex flex-wrap gap-1 mb-3">
                        {route.tags.map((tag) => (
                          <Badge key={tag} variant="secondary" className="text-xs bg-muted text-muted-foreground">
                            {tag}
                          </Badge>
                        ))}
                      </div>
                    )}

                    <div className="flex items-center justify-between pt-3 border-t border-border">
                      <button
                        className="text-xs text-muted-foreground hover:text-primary flex items-center gap-1"
                        onClick={() => fetchUserProfile(route.user_id)}
                        data-testid={`view-profile-${route.user_id}`}
                      >
                        <User className="w-3 h-3" />
                        {route.user_name || 'Anonymous'}
                      </button>
                      <div className="flex items-center gap-3">
                        <span className="text-xs text-muted-foreground">
                          {new Date(route.created_at).toLocaleDateString()}
                        </span>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 text-xs text-muted-foreground hover:text-foreground"
                          onClick={() => toggleRouteExpansion(route.id)}
                          data-testid={`toggle-comments-${route.id}`}
                        >
                          <MessageCircle className="w-3 h-3 mr-1" />
                          Comments
                          {expandedRouteId === route.id ? (
                            <ChevronUp className="w-3 h-3 ml-1" />
                          ) : (
                            <ChevronDown className="w-3 h-3 ml-1" />
                          )}
                        </Button>
                      </div>
                    </div>

                    {/* Comments Section */}
                    {expandedRouteId === route.id && (
                      <div className="mt-4 pt-4 border-t border-border">
                        {/* Add Comment */}
                        <div className="mb-4 p-3 bg-muted/30 rounded-lg">
                          <div className="flex items-center gap-2 mb-2">
                            <span className="text-sm text-foreground">Your rating:</span>
                            {renderStars(newComment.rating, true, (rating) => setNewComment({ ...newComment, rating }))}
                          </div>
                          <div className="flex gap-2">
                            <Textarea
                              placeholder="Share your experience with this route..."
                              value={newComment.content}
                              onChange={(e) => setNewComment({ ...newComment, content: e.target.value })}
                              className="bg-background border-border min-h-[60px] text-sm"
                              data-testid="comment-input"
                            />
                            <Button
                              onClick={() => handleAddComment(route.id)}
                              disabled={!newComment.content.trim()}
                              className="bg-primary hover:bg-primary/90 text-primary-foreground self-end"
                              data-testid="submit-comment-btn"
                            >
                              <Send className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>

                        {/* Comments List */}
                        {commentsLoading ? (
                          <div className="flex justify-center py-4">
                            <Loader2 className="w-5 h-5 animate-spin text-primary" />
                          </div>
                        ) : comments.length === 0 ? (
                          <p className="text-sm text-muted-foreground text-center py-4">
                            No comments yet. Be the first to review this route!
                          </p>
                        ) : (
                          <div className="space-y-3 max-h-64 overflow-auto">
                            {comments.map((comment) => (
                              <div key={comment.id} className="p-3 bg-muted/20 rounded-lg">
                                <div className="flex items-start justify-between mb-1">
                                  <div className="flex items-center gap-2">
                                    <button
                                      className="text-sm font-medium text-foreground hover:text-primary"
                                      onClick={() => fetchUserProfile(comment.user_id)}
                                    >
                                      {comment.user_name}
                                    </button>
                                    {renderStars(comment.rating)}
                                  </div>
                                  {comment.user_id === user?.id && (
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      className="h-6 w-6 text-muted-foreground hover:text-destructive"
                                      onClick={() => handleDeleteComment(route.id, comment.id)}
                                    >
                                      <X className="w-3 h-3" />
                                    </Button>
                                  )}
                                </div>
                                <p className="text-sm text-muted-foreground">{comment.content}</p>
                                <span className="text-xs text-muted-foreground/60 mt-1 block">
                                  {new Date(comment.created_at).toLocaleDateString()}
                                </span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          </ScrollArea>
        )}
      </div>

      {/* User Profile Dialog */}
      <Dialog open={profileDialogOpen} onOpenChange={setProfileDialogOpen}>
        <DialogContent className="bg-card border-border max-w-md">
          <DialogHeader>
            <DialogTitle className="text-foreground font-display flex items-center gap-2">
              <User className="w-5 h-5 text-primary" />
              User Profile
            </DialogTitle>
          </DialogHeader>

          {userProfile && (
            <div className="space-y-4">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center">
                  <span className="text-2xl font-semibold text-primary">
                    {userProfile.name?.charAt(0).toUpperCase()}
                  </span>
                </div>
                <div>
                  <h3 className="font-display text-xl text-foreground">{userProfile.name}</h3>
                  <div className="flex items-center gap-2 mt-1">
                    <Badge className={`${getReputationBadge(userProfile.reputation).color} text-white`}>
                      <Trophy className="w-3 h-3 mr-1" />
                      {getReputationBadge(userProfile.reputation).label}
                    </Badge>
                  </div>
                </div>
              </div>

              {userProfile.bio && (
                <p className="text-sm text-muted-foreground">{userProfile.bio}</p>
              )}

              <div className="grid grid-cols-3 gap-4 pt-4 border-t border-border">
                <div className="text-center">
                  <p className="text-2xl font-display font-bold text-foreground">{userProfile.shared_routes_count}</p>
                  <p className="text-xs text-muted-foreground">Shared Routes</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-display font-bold text-foreground">{userProfile.total_likes_received}</p>
                  <p className="text-xs text-muted-foreground">Likes Received</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-display font-bold text-foreground">{userProfile.total_comments}</p>
                  <p className="text-xs text-muted-foreground">Comments</p>
                </div>
              </div>

              <div className="pt-4 border-t border-border text-sm text-muted-foreground">
                <div className="flex items-center gap-2">
                  <span>Member since:</span>
                  <span className="text-foreground">{new Date(userProfile.created_at).toLocaleDateString()}</span>
                </div>
                {userProfile.favorite_vehicle_type && (
                  <div className="flex items-center gap-2 mt-1">
                    <span>Favorite vehicle:</span>
                    <span className="text-foreground capitalize">{userProfile.favorite_vehicle_type}</span>
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CommunityRoutes;
